import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="ai-embedding-status"
//
// Live-Fortschritt für den Embeddings-Tab (#5102), ein Controller pro Gruppe:
// der „Inhalte einbetten"-Submit (deckt seit dem Auto-Datei-Embedding auch
// Dateien ab) wird abgefangen und als fetch-POST abgesetzt (kein
// Full-Page-Reload mehr), danach pollt der Controller den
// embedding_status-Endpoint und schreibt Zähler, Prozente und Progress-Bars
// je Quelltyp in-place fort, bis nichts mehr aussteht.
//
// Ein laufender Backfill überlebt Reloads über einen sessionStorage-Marker —
// nach dem Neuladen (oder nach „Gruppe hinzufügen", das die Marker für die
// frisch freigeschalteten Gruppen setzt) pollt die Gruppe direkt weiter. Ohne
// Marker wird nicht gepollt: ob ein Worker läuft, ist serverseitig nicht
// abfragbar, und Dauer-Polling wäre für die Count-Queries zu teuer. Stagniert
// der Fortschritt zu lange, stoppt das Polling (Reload bleibt Fallback).
//
// Labels/URLs kommen serverseitig über data-values rein (kein i18n im JS).
//
// Lazy-Modus (#5107, lazy-value): die Übersicht rendert nur noch ein Skeleton
// (alle Quelltyp-Zeilen versteckt, Button disabled) — der Controller holt die
// Counts einmalig per Initial-Fetch vom selben Status-Endpoint, füllt die
// Tabelle, blendet Zeilen mit total=0 aus (bzw. den Leer-Hinweis ein) und gibt
// den Embed-Button anhand backfill_pending frei. delay-value staffelt die
// Initial-Fetches der Gruppen-Sektionen, damit eine Seite voller Gruppen den
// Server nicht gleichzeitig mit Count-Batterien flutet.
export default class extends Controller {
  static targets = [
    "count",
    "bar",
    "percent",
    "groupButton",
    "spinner",
    "row",
    "table",
    "loading",
    "emptyNotice",
  ];

  static values = {
    url: String,
    groupId: String,
    runningLabel: String,
    lazy: { type: Boolean, default: false },
    delay: { type: Number, default: 0 },
    interval: { type: Number, default: 3000 },
    // ~10 min Gesamtbudget, danach Stopp — reload bleibt Fallback.
    maxAttempts: { type: Number, default: 200 },
    // Stopp nach so vielen Polls ohne jede Veränderung (Worker fertig/gestorben).
    maxStale: { type: Number, default: 5 },
  };

  connect() {
    this.stopped = false;
    this.polling = false;
    this.running = this.readMarker();
    if (this.running) {
      // Der laufende Backfill pollt ohnehin — der erste Poll füllt auch das
      // Skeleton, ein zusätzlicher Initial-Fetch wäre doppelt.
      this.markRunningButtons();
      this.startPolling();
    } else if (this.lazyValue) {
      this.loadTimer = setTimeout(() => this.loadInitial(), this.delayValue);
    }
  }

  disconnect() {
    this.stopped = true;
    if (this.timer) clearTimeout(this.timer);
    if (this.loadTimer) clearTimeout(this.loadTimer);
  }

  // Einmaliger Status-Fetch fürs Skeleton (Lazy-Modus, kein Polling).
  async loadInitial() {
    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok || this.stopped) return;
      const status = await response.json();
      this.render(status);
      // Button erst freigeben, wenn klar ist, ob es etwas einzubetten gibt —
      // außer ein inzwischen gestarteter Lauf hat ihn schon übernommen.
      const button = this.groupButton();
      if (button && !this.running)
        button.disabled = status.backfill_pending === 0;
    } catch {
      // Transienter Fehler: Skeleton bleibt stehen, Reload ist der Fallback.
    }
  }

  // submit->ai-embedding-status#embed auf dem button_to-Form
  async embed(event) {
    event.preventDefault();
    const form = event.target.closest("form");

    try {
      const response = await fetch(form.action, {
        method: "POST",
        body: new FormData(form),
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      const { message } = await response.json();
      if (response.ok) {
        if (message) toastr.success(message);
        this.running = { group: true };
        this.writeMarker();
        this.markRunningButtons();
        this.startPolling();
      } else if (message) {
        toastr.error(message);
      }
    } catch {
      // Netzwerkfehler: klassischen Submit als Fallback durchlassen
      form.removeAttribute("data-action");
      form.requestSubmit();
    }
  }

  startPolling() {
    if (this.polling) return;
    this.polling = true;
    this.attempts = 0;
    this.staleCount = 0;
    this.lastSnapshot = null;
    this.showSpinner(true);
    this.schedule();
  }

  schedule() {
    this.timer = setTimeout(() => this.poll(), this.intervalValue);
  }

  async poll() {
    if (this.stopped) return;
    this.attempts += 1;

    try {
      const response = await fetch(this.urlValue, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (response.ok) {
        const status = await response.json();
        if (this.stopped) return;
        this.render(status);

        if (this.settleFinished(status)) return;

        const snapshot = JSON.stringify(status);
        this.staleCount =
          snapshot === this.lastSnapshot ? this.staleCount + 1 : 0;
        this.lastSnapshot = snapshot;
        if (this.staleCount >= this.maxStaleValue) return this.stopPolling();
      }
    } catch {
      // Netzwerk-/Transientfehler ignorieren und weiter pollen.
    }

    if (this.attempts >= this.maxAttemptsValue) return this.stopPolling();
    if (!this.stopped) this.schedule();
  }

  render({ rows }) {
    let anyContent = false;
    rows.forEach((row) => {
      const tr = this.byType(this.rowTargets, row.type);
      if (tr) tr.classList.toggle("hidden", row.total === 0);
      if (row.total > 0) anyContent = true;

      const count = this.byType(this.countTargets, row.type);
      if (count)
        count.textContent = `${this.format(row.embedded)} / ${this.format(row.total)}`;

      const bar = this.byType(this.barTargets, row.type);
      if (bar) {
        bar.style.width = `${row.percent}%`;
        bar.classList.toggle("bg-success/70", row.percent >= 100);
        bar.classList.toggle("bg-primary/70", row.percent < 100);
      }

      const percent = this.byType(this.percentTargets, row.type);
      if (percent) percent.textContent = `${row.percent} %`;
    });

    // Skeleton auflösen: Ladehinweis weg, Tabelle bzw. Leer-Hinweis zeigen.
    if (this.hasLoadingTarget) this.loadingTarget.classList.add("hidden");
    if (this.hasTableTarget)
      this.tableTarget.classList.toggle("hidden", !anyContent);
    if (this.hasEmptyNoticeTarget)
      this.emptyNoticeTarget.classList.toggle("hidden", anyContent);
  }

  // Fertig gewordenen Lauf abschließen; true, wenn nichts mehr läuft.
  settleFinished(status) {
    if (this.running?.group && status.backfill_pending === 0) {
      delete this.running.group;
      this.restoreButton(this.groupButton(), status.backfill_pending);
    }
    this.writeMarker();

    if (this.running && Object.keys(this.running).length > 0) return false;
    this.stopPolling(status);
    return true;
  }

  stopPolling(status = null) {
    this.polling = false;
    this.showSpinner(false);
    this.running = null;
    this.writeMarker();
    if (this.timer) clearTimeout(this.timer);
    // Bei Stopp ohne Abschluss (Stillstand/Timeout): Button wieder anklickbar
    this.restoreButton(
      this.groupButton(),
      status ? status.backfill_pending : 1,
    );
  }

  markRunningButtons() {
    if (this.running?.group) this.markRunning(this.groupButton());
  }

  markRunning(button) {
    if (!button) return;
    if (!button.dataset.idleLabel) button.dataset.idleLabel = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<span class="fa-solid fa-spinner fa-spin me-1"></span>${this.runningLabelValue}`;
  }

  restoreButton(button, pending) {
    if (!button || !button.dataset.idleLabel) return;
    button.innerHTML = button.dataset.idleLabel;
    delete button.dataset.idleLabel;
    button.disabled = pending === 0;
  }

  showSpinner(visible) {
    if (this.hasSpinnerTarget)
      this.spinnerTarget.classList.toggle("hidden", !visible);
  }

  groupButton() {
    return this.hasGroupButtonTarget ? this.groupButtonTarget : null;
  }

  byType(targets, type) {
    return targets.find((el) => el.dataset.embeddingType === type);
  }

  format(number) {
    try {
      return number.toLocaleString(I18n.locale.replace("_", "-"));
    } catch {
      return number.toLocaleString();
    }
  }

  // sessionStorage-Marker: ob gerade ein Backfill angestoßen wurde.
  // Zeitgestempelt, damit uralte Marker nicht ewig Polling auslösen.
  markerKey() {
    return `ka-embedding-running:${this.groupIdValue}`;
  }

  readMarker() {
    try {
      const raw = sessionStorage.getItem(this.markerKey());
      if (!raw) return null;
      const marker = JSON.parse(raw);
      if (Date.now() - marker.ts > 15 * 60 * 1000) return null;
      return marker.group ? { group: true } : null;
    } catch {
      return null;
    }
  }

  writeMarker() {
    try {
      if (this.running && Object.keys(this.running).length > 0) {
        sessionStorage.setItem(
          this.markerKey(),
          JSON.stringify({ ...this.running, ts: Date.now() }),
        );
      } else {
        sessionStorage.removeItem(this.markerKey());
      }
    } catch {
      // sessionStorage nicht verfügbar (Private Mode o.ä.) — dann eben ohne Marker
    }
  }
}
